// src/pages/actividades/components/SubtareaInlineAdd.tsx
import { useState, useRef, useEffect } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui';
import { useCrearSubtareasBatch } from '@/hooks/queries/useActividades';

interface SubtareaInlineAddProps {
  actividadPadreId: string;
  onCreated?: () => void;
}

/**
 * Inline input for quickly adding subtasks.
 * Supports batch creation with `//` separator:
 *   "subtarea 1 // subtarea 2 // subtarea 3"
 */
export function SubtareaInlineAdd({ actividadPadreId, onCreated }: SubtareaInlineAddProps) {
  const [value, setValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const batchMutation = useCrearSubtareasBatch();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;

    const titulos = trimmed
      .split('//')
      .map(s => s.trim())
      .filter(Boolean);

    if (titulos.length === 0) {
      toast.warning('Ingresa al menos un titulo de subtarea');
      return;
    }

    batchMutation.mutate(
      { actividadId: actividadPadreId, titulos },
      {
        onSuccess: () => {
          setValue('');
          onCreated?.();
          // Keep input open for more entries
          inputRef.current?.focus();
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation(); // Prevent DnD from capturing keyboard events
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      setValue('');
      setIsOpen(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="ml-8 flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-md transition-colors w-full"
        data-no-dnd="true"
      >
        <Plus className="h-3 w-3" />
        Agregar subtarea
      </button>
    );
  }

  return (
    <div className="ml-8 flex items-center gap-2 px-3 py-1.5" data-no-dnd="true">
      <Plus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (!value.trim()) setIsOpen(false);
        }}
        placeholder="Nueva subtarea (usa // para crear varias)"
        className="flex-1 text-sm bg-transparent border-none outline-none placeholder:text-muted-foreground/50"
        disabled={batchMutation.isPending}
      />
      {batchMutation.isPending && (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}
